<template>
  <div class="pagination-table">
    <!-- Search và Filters -->
    <div class="flex flex-col sm:flex-row gap-4 mb-6">
      <!-- Search -->
      <div class="flex-1">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t('BUSINESS_DATA.SEARCH.TITLE') }}
        </label>
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('BUSINESS_DATA.SEARCH.PLACEHOLDER')"
            class="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500"
            @input="onSearchInput"
          />
          <fluent-icon 
            icon="search" 
            size="16" 
            class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-woot-500"></div>
          </div>
        </div>
      </div>
      
      <!-- Category Filter -->
      <div class="w-full sm:w-48">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t('BUSINESS_DATA.FILTER.CATEGORY') }}
        </label>
        <select 
          v-model="selectedCategory" 
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500"
          @change="onFilterChange"
        >
          <option value="">{{ $t('BUSINESS_DATA.FILTER.ALL_CATEGORIES') }}</option>
          <option v-for="category in categories" :key="category" :value="category">
            {{ category || $t('BUSINESS_DATA.FILTER.UNCATEGORIZED') }}
          </option>
        </select>
      </div>
      
      <!-- Status Filter -->
      <div class="w-full sm:w-48">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t('BUSINESS_DATA.FILTER.STATUS') }}
        </label>
        <select 
          v-model="selectedStatus" 
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500"
          @change="onFilterChange"
        >
          <option value="">{{ $t('BUSINESS_DATA.FILTER.ALL_STATUS') }}</option>
          <option value="true">{{ $t('BUSINESS_DATA.FILTER.ACTIVE') }}</option>
          <option value="false">{{ $t('BUSINESS_DATA.FILTER.INACTIVE') }}</option>
        </select>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="space-y-4">
      <div v-for="i in 3" :key="i" class="animate-pulse">
        <div class="h-24 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-else-if="items.length === 0" class="text-center py-12">
      <fluent-icon :icon="emptyIcon" size="48" class="text-gray-300 mx-auto mb-4" />
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        {{ $t(emptyTitleKey) }}
      </h3>
      <p class="text-gray-500 mb-4">
        {{ $t(emptyDescriptionKey) }}
      </p>
      <woot-button @click="$emit('add-item')">
        {{ $t(addButtonKey) }}
      </woot-button>
    </div>
    
    <!-- Items List -->
    <div v-else class="space-y-4">
      <slot :items="items" :on-edit="onEdit" :on-toggle-status="onToggleStatus" :on-delete="onDelete" />
    </div>
    
    <!-- Pagination -->
    <div v-if="!isLoading && items.length > 0 && pagination.total_pages > 1" class="mt-6">
      <div class="flex items-center justify-between">
        <!-- Info -->
        <div class="text-sm text-gray-700">
          {{ $t('BUSINESS_DATA.PAGINATION.SHOWING', {
            start: ((pagination.current_page - 1) * pagination.per_page) + 1,
            end: Math.min(pagination.current_page * pagination.per_page, pagination.total_count),
            total: pagination.total_count
          }) }}
        </div>
        
        <!-- Pagination Controls -->
        <div class="flex items-center space-x-2">
          <!-- Previous Button -->
          <woot-button
            variant="clear"
            size="small"
            :disabled="!pagination.has_prev_page"
            @click="goToPage(pagination.current_page - 1)"
          >
            <fluent-icon icon="chevron-left" size="14" />
            {{ $t('BUSINESS_DATA.PAGINATION.PREVIOUS') }}
          </woot-button>
          
          <!-- Page Numbers -->
          <div class="flex items-center space-x-1">
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="[
                'px-3 py-1 text-sm rounded-md transition-colors',
                page === pagination.current_page
                  ? 'bg-woot-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              ]"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </div>
          
          <!-- Next Button -->
          <woot-button
            variant="clear"
            size="small"
            :disabled="!pagination.has_next_page"
            @click="goToPage(pagination.current_page + 1)"
          >
            {{ $t('BUSINESS_DATA.PAGINATION.NEXT') }}
            <fluent-icon icon="chevron-right" size="14" />
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaginationTable',
  
  props: {
    items: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    },
    emptyIcon: {
      type: String,
      default: 'document'
    },
    emptyTitleKey: {
      type: String,
      default: 'BUSINESS_DATA.EMPTY.TITLE'
    },
    emptyDescriptionKey: {
      type: String,
      default: 'BUSINESS_DATA.EMPTY.DESCRIPTION'
    },
    addButtonKey: {
      type: String,
      default: 'BUSINESS_DATA.ADD_NEW'
    }
  },
  
  emits: ['search', 'filter-change', 'page-change', 'edit-item', 'toggle-status', 'delete-item', 'add-item'],
  
  data() {
    return {
      searchQuery: '',
      selectedCategory: '',
      selectedStatus: '',
      searchTimeout: null
    };
  },
  
  computed: {
    visiblePages() {
      const current = this.pagination.current_page;
      const total = this.pagination.total_pages;
      const delta = 2; // Show 2 pages before and after current
      
      const range = [];
      const rangeWithDots = [];
      
      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i += 1) {
        range.push(i);
      }
      
      if (current - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }
      
      rangeWithDots.push(...range);
      
      if (current + delta < total - 1) {
        rangeWithDots.push('...', total);
      } else if (total > 1) {
        rangeWithDots.push(total);
      }
      
      return rangeWithDots;
    }
  },
  
  methods: {
    onSearchInput() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.searchQuery);
      }, 500);
    },
    
    onFilterChange() {
      this.$emit('filter-change', {
        category: this.selectedCategory,
        status: this.selectedStatus
      });
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.total_pages && page !== this.pagination.current_page) {
        this.$emit('page-change', page);
      }
    },
    
    onEdit(item) {
      this.$emit('edit-item', item);
    },
    
    onToggleStatus(item) {
      this.$emit('toggle-status', item);
    },
    
    onDelete(item) {
      this.$emit('delete-item', item);
    }
  }
};
</script>
